/**
 * reorder.js — "Beställ igen" (Agent E). Vanilla ES2019, defer.
 *
 * Lyssnar (delegation) på alla [data-jolat-reorder]-knappar. Vid klick:
 *   1. Läser data-items='[{"id":<variantId>,"quantity":<q>},...]' (kontrakt §6-E).
 *   2. Lägger allt i varukorgen via Jolat.cart.add (bulk).
 *   3. Vid fel: en-och-en-fallback så otillgängliga varianter hoppas över.
 *   4. Resultat-toast "✓ N tillagda (M saknas)" + öppnar cart-drawern.
 *
 * i18n: läser <script type="application/json" data-jolat-reorder-i18n> med
 *   { success: "...__COUNT__...", partial: "...__ADDED__...__MISSING__...",
 *     loading: "...", error: "..." }. (Renderas av customer-dashboard/main-order.)
 *
 * Beroenden: window.Jolat (Agent G). Faller tillbaka till rå fetch om Jolat saknas.
 */
(function () {
  'use strict';

  var i18n = (function () {
    var el = document.querySelector('[data-jolat-reorder-i18n]');
    var defaults = {
      success: '__COUNT__ produkter tillagda i varukorgen',
      partial: '__ADDED__ tillagda (__MISSING__ saknas)',
      loading: 'Lägger till …',
      error: 'Inga produkter kunde läggas till'
    };
    if (!el) return defaults;
    try {
      var parsed = JSON.parse(el.textContent);
      return {
        success: parsed.success || defaults.success,
        partial: parsed.partial || defaults.partial,
        loading: parsed.loading || defaults.loading,
        error: parsed.error || defaults.error
      };
    } catch (e) {
      return defaults;
    }
  })();

  function fill(tpl, map) {
    var out = tpl || '';
    Object.keys(map).forEach(function (key) {
      out = out.split(key).join(String(map[key]));
    });
    return out;
  }

  /* ---- Toast ----------------------------------------------------------- */
  var toastTimer = null;
  function toast(message, kind) {
    var host = document.querySelector('[data-jolat-toast-host]');
    if (!host) {
      host = document.createElement('div');
      host.setAttribute('data-jolat-toast-host', '');
      host.className = 'jl-toast-host';
      document.body.appendChild(host);
    }
    var node = document.createElement('div');
    node.className = 'jl-toast' + (kind === 'error' ? ' jl-toast--error' : '');
    node.setAttribute('role', 'status');
    node.textContent = message;
    host.appendChild(node);
    // Forcera reflow så transition triggas
    void node.offsetWidth;
    node.classList.add('is-shown');
    window.clearTimeout(toastTimer);
    var remove = function () {
      node.classList.remove('is-shown');
      window.setTimeout(function () {
        if (node.parentNode) node.parentNode.removeChild(node);
      }, 250);
    };
    toastTimer = window.setTimeout(remove, kind === 'error' ? 6000 : 4000);
    node.addEventListener('click', remove);
  }

  /* ---- Cart-helpers (Jolat med fetch-fallback) ------------------------- */
  function addBulk(items) {
    if (window.Jolat && window.Jolat.cart && window.Jolat.cart.add) {
      return window.Jolat.cart.add(items);
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { return Promise.reject(e); });
      return r.json();
    }).then(function (cart) {
      document.dispatchEvent(new CustomEvent('jolat:cart:updated', { detail: cart }));
      document.dispatchEvent(new CustomEvent('jolat:cart:open'));
      return cart;
    });
  }

  function addOne(item) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: item.id, quantity: item.quantity })
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  function notifyCartChanged() {
    // Hämta färsk varukorg och broadcasta så drawer/badge uppdateras
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('jolat:cart:updated', { detail: cart }));
      })
      .catch(function () {});
  }

  /* ---- Knappläge ------------------------------------------------------- */
  function setLabel(btn, text) {
    var label = btn.querySelector('.jl-reorder__label');
    if (label) label.textContent = text;
    else btn.textContent = text;
  }

  function handle(btn) {
    if (btn.disabled || btn.dataset.state === 'loading') return;

    var items;
    try {
      items = JSON.parse(btn.getAttribute('data-items') || '[]');
    } catch (err) {
      toast(i18n.error, 'error');
      return;
    }
    items = (Array.isArray(items) ? items : [])
      .filter(function (it) { return it && it.id; })
      .map(function (it) { return { id: it.id, quantity: Math.max(1, parseInt(it.quantity, 10) || 1) }; });
    if (items.length === 0) {
      toast(i18n.error, 'error');
      return;
    }

    var original = (btn.querySelector('.jl-reorder__label') || btn).textContent;
    btn.dataset.state = 'loading';
    btn.disabled = true;
    setLabel(btn, i18n.loading);

    addBulk(items)
      .then(function () {
        finish(btn, original, items.length, 0);
        if (window.Jolat && window.Jolat.openCart) window.Jolat.openCart();
        else document.dispatchEvent(new CustomEvent('jolat:cart:open'));
      })
      .catch(function () {
        retryOneByOne(items, btn, original);
      });
  }

  function retryOneByOne(items, btn, original) {
    var added = 0;
    var chain = items.reduce(function (p, item) {
      return p.then(function () {
        return addOne(item).then(function (ok) { if (ok) added += 1; });
      });
    }, Promise.resolve());

    chain.then(function () {
      var missing = items.length - added;
      if (added > 0) {
        notifyCartChanged();
        finish(btn, original, added, missing);
        if (window.Jolat && window.Jolat.openCart) window.Jolat.openCart();
        else document.dispatchEvent(new CustomEvent('jolat:cart:open'));
      } else {
        btn.dataset.state = 'error';
        btn.disabled = false;
        setLabel(btn, original);
        toast(i18n.error, 'error');
      }
    });
  }

  function finish(btn, original, added, missing) {
    btn.dataset.state = 'done';
    var message = missing > 0
      ? fill(i18n.partial, { __ADDED__: added, __MISSING__: missing })
      : fill(i18n.success, { __COUNT__: added });
    toast(message, missing > 0 ? 'error' : 'ok');
    setLabel(btn, '✓');
    window.setTimeout(function () {
      btn.dataset.state = 'idle';
      btn.disabled = false;
      setLabel(btn, original);
    }, 2500);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('[data-jolat-reorder]');
    if (!btn) return;
    e.preventDefault();
    handle(btn);
  });
})();
